import { PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSessionStorage } from 'react-use';

import { Box, DialogTitle, Stack, Theme, Typography, useMediaQuery } from '@mui/material';

import Image from 'next/image';
import { useRouter } from 'next/router';
import { pathToRegexp } from 'path-to-regexp';
import { useIsMounted } from 'usehooks-ts';

import { AnalyticsContext, EVENTS, SignupFormComponent, useTrack } from '@arrived/analytics';
import { nextImageKitLoader, ROUTES, SessionStorageKeys } from '@arrived/common';
import { SUPPORTED_RICH_TEXT_FEATURES, useIsMobile } from '@arrived/components';
import { FEATURE_FLAGS, useFeature } from '@arrived/feature-flags';
import { AvailabilityFilter } from '@arrived/models';
import { useOfferingQuery, useOfferingSearchQuery } from '@arrived/queries';

import { OfferingViewLimitContext } from '../OfferingViewLimitContext';
import { SignupDialog } from '../Signup';

const offerings = [
  { id: 112, time: '4hrs' },
  { id: 106, time: '4.5hrs' },
  { id: 213, time: '2 days' },
];

const SelloutImage = ({ id, time }: { id: number; time: string }) => {
  const offeringState = useOfferingQuery(id);

  return (
    <>
      {offeringState.isSuccess && (
        <Box position="relative" pb={3}>
          <Image
            loader={nextImageKitLoader}
            src={offeringState.data.properties[0]?.photos[0].url}
            width={105}
            height={63}
            alt={offeringState.data.name}
          />
          <Typography
            fontWeight="semibold"
            position="absolute"
            bottom={0}
            left="50%"
            noWrap
            sx={{ transform: 'translateX(-50%)' }}
          >
            <FormattedMessage id="sold-out-in-time" defaultMessage="Sold out in {time}" values={{ time }} />
          </Typography>
        </Box>
      )}
    </>
  );
};

export const SoldOutPopup = ({ children }: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const { hasAnonymousUserLoggedIn } = useContext(AnalyticsContext);
  // TODO: Instead of toggling between these with state we should have a unified "PropertyGridPopup" component that
  //  maintains state as to the current popup that is showing. Otherwise we'll have the wild wild west of different
  //  popups in different places.
  const { isPopupShowing: isViewLimitPopupShowing } = useContext(OfferingViewLimitContext);
  const [hasClosedPopup, setHasClosedPopup] = useSessionStorage(SessionStorageKeys.HAS_CLOSED_SIGNUP_POPUP, false);
  const forSaleOfferingState = useOfferingSearchQuery({
    availability: { eq: [AvailabilityFilter.FOR_SALE] },
    fields: ['id'],
    size: 1,
  });
  const isSmallScreen = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'));
  const isMobile = useIsMobile();
  const soldOutPopUpFeature = useFeature<number>(FEATURE_FLAGS.SOLD_OUT_POP_UP);
  const isMounted = useIsMounted();
  const track = useTrack();
  const { pathname } = useRouter();
  // Intention here is to only show the pop-up on /properties, this will likely need to change if we end up adding
  // url params to the /properties page.
  const pathnameMatches = useMemo(
    () => Boolean(pathname.match(pathToRegexp(ROUTES.properties.base, [], { start: false }))),
    [pathname]
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setHasClosedPopup(true);
    track(EVENTS.SIGNUP_FORM_DISPLAYED, { action: 'close', component: SignupFormComponent.SOLD_OUT_POP_UP });
  }, [track]);

  // We need to store this function in a ref so that when it's invoked at the timeout the value of
  // isViewLimitPopupShowing is accurate
  const openDialog = useRef<() => void>();
  openDialog.current = useCallback(() => {
    if (isMounted() && !isViewLimitPopupShowing) {
      setIsOpen(true);
      track(EVENTS.SIGNUP_FORM_DISPLAYED, { action: 'open', component: SignupFormComponent.SOLD_OUT_POP_UP });
    }
  }, [isMounted, isViewLimitPopupShowing, track]);

  const arePropertiesAvailable = useMemo(
    () => (forSaleOfferingState.isSuccess ? forSaleOfferingState.data.pagination.totalResults > 0 : undefined),
    [forSaleOfferingState]
  );

  useEffect(() => {
    let timeout;

    if (
      ((soldOutPopUpFeature.value === 1 && arePropertiesAvailable === false) || soldOutPopUpFeature.value === 2) &&
      hasAnonymousUserLoggedIn === false &&
      !hasClosedPopup &&
      pathnameMatches
    ) {
      timeout = setTimeout(() => {
        openDialog.current?.();
      }, 5_000);
    }

    return () => clearTimeout(timeout);
  }, [arePropertiesAvailable, hasAnonymousUserLoggedIn, hasClosedPopup, pathnameMatches, soldOutPopUpFeature.value]);

  return (
    <>
      <SignupDialog
        accountCreationEventProperties={{ component: SignupFormComponent.SOLD_OUT_POP_UP }}
        close={close}
        DialogContentProps={{
          children: (
            <Typography
              variant="h5"
              textAlign="center"
              fontSize={({ fontSizes }) => ({
                xs: fontSizes['lg'],
                md: fontSizes['2xl'],
              })}
              lineHeight={({ lineHeights }) => ({
                xs: lineHeights['lg'],
                md: lineHeights['2xl'],
              })}
            >
              <FormattedMessage
                id="diligence-threshold-signup-notified"
                defaultMessage="We buy less than 0.2% of properties.{br}<strong>Sign up to be notified on the next drop.</strong>"
                values={SUPPORTED_RICH_TEXT_FEATURES}
              />
            </Typography>
          ),
          sx: {
            maxHeight: '700px',
          },
        }}
        fullScreen={isMobile}
        open={isOpen}
        sx={{
          '& .MuiDialog-paper.MuiPaper-root': {
            maxWidth: '800px',
            px: { xs: 1, md: 4 },
            pt: 6,
            pb: { xs: 4, md: 6 },
            justifyContent: 'center',
          },
        }}
        SignupFormProps={{
          LoadingButtonProps: { sx: { mt: { xs: 'auto', md: 2 } } },
          sx: { flexGrow: { xs: 1, md: 0 } },
        }}
      >
        <Stack direction="row" gap={5} justifyContent="center" pb={2}>
          {offerings.slice(0, isSmallScreen ? 1 : undefined).map((offering) => (
            <SelloutImage key={offering.id} {...offering} />
          ))}
        </Stack>
        <DialogTitle variant="h3.bold" textAlign="center" sx={{ px: 0, py: 0, mb: 1 }}>
          <FormattedMessage id="no-properties-available" defaultMessage="No properties available?" />
        </DialogTitle>
      </SignupDialog>
      {children}
    </>
  );
};
